/**
 * Effects picked from the Waimers Aceternity previews. Each one looks for
 * its own hook and does nothing when the page has none, so this file is safe
 * to load everywhere (layout/theme.liquid). Pointer effects run only with a
 * fine pointer; everything is off or static under reduced motion.
 *
 *  1 Lens zoom        - product gallery main image (desktop)
 *  2 3D tilt          - [data-tilt] (the Latest drop product panel)
 *  7 Tracing beam     - the roadmap timeline (.roadmap)
 *  8 Card stack       - [data-card-stack] (About: review stack)
 *  9 Meteors          - the newsletter card
 *
 * 4 (focus cards), 5 (expanding panels) and 11 (tooltips) are CSS only.
 * Lamp, text generate and holo glare were removed in the human pass.
 */
(function () {
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');

  function onView(el, cb, threshold) {
    if (!('IntersectionObserver' in window)) { cb(); return; }
    var io = new IntersectionObserver(function (entries) {
      if (entries[0].isIntersecting) { io.disconnect(); cb(); }
    }, { threshold: threshold || 0.4 });
    io.observe(el);
  }

  /* 1 Lens zoom ------------------------------------------------------ */
  function initLens(root) {
    (root || document).querySelectorAll('.product-gallery__main').forEach(function (main) {
      if (main.dataset.lens) return;
      main.dataset.lens = 'true';
      var lens = document.createElement('span');
      lens.className = 'lens-zoom';
      lens.setAttribute('aria-hidden', 'true');
      main.appendChild(lens);
      var Z = 2.4;
      main.addEventListener('pointermove', function (e) {
        if (!fine.matches || window.innerWidth < 990) return;
        var img = main.querySelector('.product-gallery__slide.is-active img');
        if (!img) return;
        var b = img.getBoundingClientRect();
        var x = e.clientX - b.left;
        var y = e.clientY - b.top;
        if (x < 0 || y < 0 || x > b.width || y > b.height) { lens.classList.remove('is-on'); return; }
        var mb = main.getBoundingClientRect();
        lens.style.left = e.clientX - mb.left + 'px';
        lens.style.top = e.clientY - mb.top + 'px';
        var src = img.currentSrc || img.src;
        if (lens.dataset.src !== src) { lens.dataset.src = src; lens.style.backgroundImage = 'url("' + src + '")'; }
        lens.style.backgroundSize = b.width * Z + 'px ' + b.height * Z + 'px';
        lens.style.backgroundPosition = (-x * Z + 70) + 'px ' + (-y * Z + 70) + 'px';
        lens.classList.add('is-on');
      });
      main.addEventListener('pointerleave', function () { lens.classList.remove('is-on'); });
    });
  }

  /* 2 3D tilt -------------------------------------------------------- */
  function initTilt(root) {
    (root || document).querySelectorAll('[data-tilt]').forEach(function (el) {
      if (el.dataset.tiltOn) return;
      el.dataset.tiltOn = 'true';
      el.addEventListener('pointermove', function (e) {
        if (!fine.matches || reduce.matches) return;
        var b = el.getBoundingClientRect();
        var px = (e.clientX - b.left) / b.width - 0.5;
        var py = (e.clientY - b.top) / b.height - 0.5;
        el.style.transform = 'rotateY(' + px * 12 + 'deg) rotateX(' + -py * 12 + 'deg)';
      });
      el.addEventListener('pointerleave', function () { el.style.transform = ''; });
    });
  }

  /* 7 Tracing beam --------------------------------------------------- */
  function initTrace(root) {
    (root || document).querySelectorAll('.roadmap').forEach(function (list) {
      if (list.dataset.trace) return;
      list.dataset.trace = 'true';
      list.classList.add('has-trace');
      var markers = Array.prototype.slice.call(list.querySelectorAll('.roadmap__marker'));
      if (markers.length < 2) return;
      var ticking = false;
      function centre(m) {
        var lb = list.getBoundingClientRect();
        var mb = m.getBoundingClientRect();
        return mb.top + mb.height / 2 - lb.top;
      }
      function update() {
        ticking = false;
        var start = centre(markers[0]);
        var end = centre(markers[markers.length - 1]);
        var lb = list.getBoundingClientRect();
        var line = window.innerHeight * 0.55 - lb.top;
        var reach = Math.max(0, Math.min(end - start, line - start));
        list.style.setProperty('--trace-start', start + 'px');
        list.style.setProperty('--trace', reach + 'px');
        markers.forEach(function (m) {
          var entry = m.closest('.roadmap__entry');
          if (entry) entry.classList.toggle('is-traced', centre(m) - start <= reach + 1);
        });
      }
      window.addEventListener('scroll', function () {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* 8 Card stack ----------------------------------------------------- */
  function initStack(root) {
    (root || document).querySelectorAll('[data-card-stack]').forEach(function (stack) {
      if (stack.dataset.stackOn) return;
      stack.dataset.stackOn = 'true';
      var cards = Array.prototype.slice.call(stack.children);
      if (cards.length < 2) return;
      function layout() {
        cards.forEach(function (c, i) {
          c.style.setProperty('--i', i);
          c.classList.toggle('is-top', i === 0);
          c.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
        });
      }
      layout();
      var timer = null;
      function next() { cards.push(cards.shift()); layout(); }
      function play() { if (!reduce.matches && !timer) timer = window.setInterval(next, 3600); }
      function pause() { window.clearInterval(timer); timer = null; }
      stack.addEventListener('mouseenter', pause);
      stack.addEventListener('mouseleave', play);
      stack.addEventListener('click', next);
      onView(stack, play, 0.3);
    });
  }

  /* 9 Meteors -------------------------------------------------------- */
  function initMeteors(root) {
    (root || document).querySelectorAll('.newsletter__card').forEach(function (card) {
      if (card.dataset.meteors || reduce.matches) return;
      card.dataset.meteors = 'true';
      var box = document.createElement('span');
      box.className = 'meteors';
      box.setAttribute('aria-hidden', 'true');
      for (var i = 0; i < 16; i++) {
        var m = document.createElement('span');
        m.className = 'meteor';
        m.style.left = Math.floor(Math.random() * 110) + '%';
        m.style.animationDelay = (Math.random() * 6).toFixed(2) + 's';
        m.style.animationDuration = (3 + Math.random() * 5).toFixed(2) + 's';
        box.appendChild(m);
      }
      card.insertBefore(box, card.firstChild);
    });
  }

  /* 10 Text generate ------------------------------------------------- */

  function init(root) {
    initLens(root);
    initTilt(root);
    initTrace(root);
    initStack(root);
    initMeteors(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
